import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, Plus, Minus, Stethoscope } from 'lucide-react';
import { CLINIC, IMAGES, EDU_ARTICLES } from '../mock';
import { PageHero } from '../components/Shared';

export default function Article() {
  const { slug } = useParams();
  const article = EDU_ARTICLES.find((a) => a.slug === slug);
  const [open, setOpen] = useState(0);

  if (!article) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-4">
        <div>
          <h1 className="font-display text-3xl text-teal-900 mb-3" style={{ fontWeight: 600 }}>Article not found</h1>
          <Link to="/patient-education" className="btn-teal inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold"><ArrowLeft size={16} /> Back to Patient Education</Link>
        </div>
      </div>
    );
  }

  const related = EDU_ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div>
      <PageHero eyebrow={article.eyebrow} title={article.title} subtitle={article.excerpt} image={IMAGES[article.image]} />

      <article className="py-16">
        <div className="container-x max-w-3xl">
          <Link to="/patient-education" className="inline-flex items-center gap-2 text-teal-700 text-sm font-medium mb-8 hover:gap-3 transition-all"><ArrowLeft size={16} /> Patient Education</Link>

          <p className="text-lg text-slate-700 leading-relaxed mb-10">{article.intro}</p>

          {article.sections.map((s) => (
            <section key={s.h} className="mb-9">
              <h2 className="font-display text-2xl text-teal-900 mb-3" style={{ fontWeight: 600 }}>{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-3">{para}</p>
              ))}
              {s.list && (
                <ul className="space-y-2 mt-3">
                  {s.list.map((li) => (
                    <li key={li} className="flex gap-3 text-slate-700"><CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" /> <span>{li}</span></li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Callout */}
          <div className="rounded-2xl border border-teal-100 bg-teal-50/60 p-6 flex gap-4 my-10">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-600 text-white shrink-0"><AlertCircle size={22} /></span>
            <div>
              <h3 className="font-semibold text-teal-900 mb-1">{article.callout.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{article.callout.body}</p>
            </div>
          </div>

          {/* FAQs */}
          <h2 className="font-display text-2xl text-teal-900 mb-4" style={{ fontWeight: 600 }}>Frequently asked questions</h2>
          <div className="space-y-3 mb-12">
            {article.faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className={`bg-white rounded-2xl border transition-colors ${isOpen ? 'border-teal-200' : 'border-teal-50'}`}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex items-center justify-between w-full text-left p-5">
                    <span className="font-semibold text-teal-900 pr-4">{f.q}</span>
                    <span className={`grid place-items-center w-8 h-8 rounded-full shrink-0 transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700'}`}>{isOpen ? <Minus size={16} /> : <Plus size={16} />}</span>
                  </button>
                  <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                    <div className="overflow-hidden"><p className="px-5 pb-5 text-slate-600 leading-relaxed">{f.a}</p></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="deep-section rounded-3xl p-8 text-center relative overflow-hidden">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white/10 text-white mx-auto mb-4"><Stethoscope size={26} /></span>
            <h3 className="font-display text-2xl text-white mb-2" style={{ fontWeight: 600 }}>Consult Dr Puneet Srivastava</h3>
            <p className="text-teal-100/85 mb-6 max-w-xl mx-auto">UK-trained consultant rheumatologist — Srigyan Rheumatology Clinic, Ghaziabad &amp; Noida.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> Call {CLINIC.phone}</a>
              <Link to="/second-opinion" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors">Book a Review <ArrowRight size={18} /></Link>
            </div>
          </div>

          {/* Related */}
          <h3 className="font-display text-xl text-teal-900 mt-14 mb-5" style={{ fontWeight: 600 }}>Related questions</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/patient-education/${r.slug}`} className="card-lift block bg-white rounded-2xl border border-teal-50 p-5">
                <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{r.eyebrow}</span>
                <p className="text-teal-900 font-medium mt-2 leading-snug">{r.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
